import type { Ref } from 'react';
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';
import clsx from 'clsx';

export type ButtonVariant =
  'neutral' | 'primary' | 'secondary' | 'ghost' | 'error';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';

// Full class names, never built by concatenation, so Tailwind's scanner sees them.
const VARIANT_CLASS: Record<ButtonVariant, string> = {
  neutral: '',
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  ghost: 'btn-ghost',
  error: 'btn-error',
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  xs: 'btn-xs',
  sm: 'btn-sm',
  md: '',
  lg: 'btn-lg',
};

/** daisyUI `btn` classes for a variant and size; shared with `LinkButton`. */
export function buttonClasses(
  variant: ButtonVariant,
  size: ButtonSize,
  className?: string,
): string {
  return clsx('btn', VARIANT_CLASS[variant], SIZE_CLASS[size], className);
}

export interface ButtonProps extends Omit<AriaButtonProps, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  ref?: Ref<HTMLButtonElement>;
}

/** React Aria `Button` (keyboard, press and disabled semantics) in daisyUI's `btn` look. */
export function Button({
  variant = 'neutral',
  size = 'md',
  className,
  ...rest
}: ButtonProps) {
  return (
    <AriaButton {...rest} className={buttonClasses(variant, size, className)} />
  );
}
